import { useEffect, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { SectionCard } from "@/components/dashboard/shared/SectionCard";
import { dashboardStyles } from "@/components/dashboard/shared/dashboard-styles";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RiSearchLine, RiStackLine } from "@remixicon/react";
import api from "@/utils/api";
import { useCurrentStore } from "@/hooks/useCurrentStore";
import { Skeleton } from "@/components/ui/skeleton";
import { MOVEMENT_TYPE_LABELS } from "@/lib/permissions";

type Movement = {
  _id: string;
  productName: string;
  variantLabel?: string;
  sku?: string;
  type: string;
  direction: "in" | "out";
  quantity: number;
  previousQuantity: number;
  newQuantity: number;
  note?: string;
  referenceNumber?: string;
  performedByName?: string;
  createdAt: string;
};

const TYPE_FILTERS = [
  { value: "all", label: "All types" },
  { value: "restock", label: "Restock" },
  { value: "reduction", label: "Reduce stock" },
  { value: "damage", label: "Damage / loss" },
  { value: "return", label: "Returns" },
  { value: "count_correction", label: "Count correction" },
  { value: "order", label: "Orders" },
  { value: "invoice", label: "Invoices" },
  { value: "pos_sale", label: "POS sales" },
];

export function InventoryHistory() {
  const { storeId } = useCurrentStore();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [type, setType] = useState("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 200);
    return () => window.clearTimeout(timeout);
  }, [searchInput]);

  const { data, isLoading } = useQuery({
    queryKey: ["inventory-movements", storeId, search, type, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (type !== "all") params.set("type", type);
      params.set("page", String(page));
      params.set("limit", "25");
      const { data: response } = await api.get(`/api/inventory/movements?${params}`);
      return response as {
        items: Movement[];
        pagination: { page: number; pages: number; total: number };
      };
    },
    enabled: !!storeId,
    placeholderData: keepPreviousData,
  });

  const items = data?.items || [];
  const pagination = data?.pagination || { page: 1, pages: 1, total: 0 };

  return (
    <SectionCard>
      <div className="mb-4 flex flex-col gap-3 lg:flex-row">
        <div className="relative min-w-0 flex-1">
          <RiSearchLine className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search product, SKU, staff, or reference"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            className={dashboardStyles.filterInput}
          />
        </div>
        <Select
          value={type}
          onValueChange={(value) => {
            setType(value);
            setPage(1);
          }}
        >
          <SelectTrigger className={`${dashboardStyles.filterSelect} w-full min-w-[160px] sm:w-auto`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TYPE_FILTERS.map((filter) => (
              <SelectItem key={filter.value} value={filter.value}>
                {filter.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading && !data ? (
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-14 w-full rounded-2xl" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl bg-muted/20 px-4 py-16">
          <div className={dashboardStyles.emptyIcon}>
            <RiStackLine className="h-4 w-4 text-muted-foreground" />
          </div>
          <p className={dashboardStyles.emptyTitle}>No inventory records yet</p>
          <p className={dashboardStyles.emptyDescription}>
            Restocks, reductions, orders, invoices, and POS sales will show up here.
          </p>
        </div>
      ) : (
        <div className={dashboardStyles.tableWrap}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Qty</TableHead>
                <TableHead>Stock</TableHead>
                <TableHead>By</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item._id}>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                    {new Date(item.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <p className="font-medium text-foreground">{item.productName}</p>
                    <p className="text-xs text-muted-foreground">
                      {item.variantLabel || item.sku || "Default"}
                      {item.referenceNumber ? ` · ${item.referenceNumber}` : ""}
                    </p>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="secondary"
                      className={
                        item.direction === "in"
                          ? "rounded-full border-0 bg-emerald-50 text-emerald-700"
                          : "rounded-full border-0 bg-amber-50 text-amber-800"
                      }
                    >
                      {MOVEMENT_TYPE_LABELS[item.type] || item.type}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-semibold tabular-nums">
                    {item.direction === "in" ? "+" : "−"}
                    {item.quantity}
                  </TableCell>
                  <TableCell className="text-xs tabular-nums text-muted-foreground">
                    {item.previousQuantity} → {item.newQuantity}
                  </TableCell>
                  <TableCell>
                    <p className="text-sm">{item.performedByName || "System"}</p>
                    {item.note ? (
                      <p className="max-w-[180px] truncate text-xs text-muted-foreground">{item.note}</p>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {pagination.pages > 1 ? (
        <div className={dashboardStyles.paginationBar}>
          <p>
            Page {pagination.page} of {pagination.pages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className={dashboardStyles.paginationBtn}
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              className={dashboardStyles.paginationBtn}
              disabled={page === pagination.pages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </SectionCard>
  );
}
